import { Alert, Button, Form, Input, Radio, Segmented, message } from 'antd';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthHeader } from '../components/AuthHeader';
import { CheckCircleOutlined, IdcardOutlined } from '../icons';
import { ApiError } from '../lib/http';
import { register, registrationStatus, resubmitRegistration } from '../services/auth';
import type { RegistrationStatus } from '../types/api';

type Mode = 'register' | 'status';

const ROLE_OPTIONS: Array<{ value: 'student' | 'teacher'; label: string }> = [
  { value: 'student', label: '学生' },
  { value: 'teacher', label: '教师' },
];

/** 自助注册统一使用 G＋身份证号码；学生与教师均需人工审核通过后才能登录。 */
export function RegisterPage() {
  const nav = useNavigate();
  const [mode, setMode] = useState<Mode>('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [status, setStatus] = useState<RegistrationStatus | null>(null);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [resubmitForm] = Form.useForm();

  async function submitRegister() {
    const v = await form.validateFields();
    setLoading(true);
    setError('');
    setDone('');
    try {
      const res = await register(v);
      message.success(res.message);
      setDone(res.message);
      setStatus({ status: 'pending', submitted_at: new Date().toISOString() });
      setMode('status');
      statusForm.setFieldsValue({ principal: v.principal, password: v.password });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }

  async function queryStatus() {
    const v = await statusForm.validateFields();
    setLoading(true);
    setError('');
    try {
      const res = await registrationStatus(v.principal, v.password);
      setStatus(res);
      resubmitForm.setFieldsValue({
        name: res.name || '',
        role: res.role || 'student',
        class_name: res.class_name || '',
      });
    } catch (e) {
      setStatus(null);
      setError(e instanceof ApiError ? e.message : '查询失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }

  async function submitResubmit() {
    const v = await statusForm.validateFields();
    const r = await resubmitForm.validateFields();
    setLoading(true);
    setError('');
    try {
      const res = await resubmitRegistration({
        principal: v.principal,
        password: v.password,
        name: r.name,
        role: r.role,
        class_name: r.class_name,
      });
      message.success(res.message);
      setDone(res.message);
      setStatus({ status: 'pending', submitted_at: new Date().toISOString() });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '重新提交失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <AuthHeader />
      <main className="auth-main">
        <section className="auth-intro">
          <p className="auth-kicker">账号注册</p>
          <h1>注册后需实验员审核</h1>
          <p>学生与教师使用同一入口。账号为 G＋身份证号码，审核通过后即可登录预约。</p>
          <ul className="auth-points">
            <li>
              <CheckCircleOutlined /> 账号格式：G＋18 位身份证号码
            </li>
            <li>
              <CheckCircleOutlined /> 姓名须与学籍或教师档案一致
            </li>
            <li>
              <CheckCircleOutlined /> 被驳回可查看原因并修改重提
            </li>
          </ul>
        </section>

        <div className="login-card auth-card auth-card-wide">
          <h1>注册账号</h1>
          <p className="auth-subtitle">学生、教师均可注册，提交后等待审核</p>
          <Segmented
          block
          value={mode}
          onChange={(v) => {
            setMode(v as Mode);
            setError('');
          }}
          options={[
            { label: '注册', value: 'register' },
            { label: '审核状态', value: 'status' },
          ]}
        />
        {done ? <Alert style={{ marginTop: 16 }} type="success" showIcon title={done} /> : null}
        {error ? <Alert style={{ marginTop: 16 }} type="error" showIcon title={error} /> : null}

        {mode === 'register' ? (
          <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ role: 'student' }}>
            <Form.Item
              name="principal"
              label="账号（G＋身份证号码）"
              extra="学生与教师通用，末位 X 不区分大小写"
              rules={[
                { required: true, message: '请输入 G＋身份证号码' },
                { pattern: /^[gG]\d{17}[\dXx]$/, message: '格式应为 G 开头加 18 位身份证号码' },
              ]}
            >
              <Input prefix={<IdcardOutlined />} autoComplete="username" placeholder="G + 18 位身份证号码" />
            </Form.Item>
            <Form.Item
              name="name"
              label="姓名"
              rules={[{ required: true, message: '请输入真实姓名' }, { max: 50 }]}
            >
              <Input autoComplete="name" />
            </Form.Item>
            <Form.Item name="role" label="身份">
              <Radio.Group options={ROLE_OPTIONS} />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.role !== cur.role}
            >
              {({ getFieldValue }) => {
                const role = getFieldValue('role') as 'student' | 'teacher' | undefined;
                return (
                  <Form.Item
                    name="class_name"
                    label={role === 'teacher' ? '教研组' : '班级'}
                    extra={role === 'teacher' ? '如：信息技术组' : '如：高一二班，便于实验员安排'}
                    rules={[{ max: 30 }]}
                  >
                    <Input placeholder={role === 'teacher' ? '信息技术组' : '高一二班'} />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item
              name="password"
              label="密码"
              extra="至少 8 位，建议同时包含字母和数字"
              rules={[{ required: true, min: 8, message: '密码至少 8 位' }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入密码' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || value === getFieldValue('password')
                      ? Promise.resolve()
                      : Promise.reject(new Error('两次输入的密码不一致')),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" block className="touch" loading={loading} onClick={submitRegister}>
              提交注册
            </Button>
          </Form>
        ) : (
          <Form form={statusForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="principal"
              label="账号（G＋身份证号码）"
              rules={[{ required: true, message: '请输入 G＋身份证号码' }]}
            >
              <Input prefix={<IdcardOutlined />} autoComplete="username" />
            </Form.Item>
            <Form.Item name="password" label="注册时设置的密码" rules={[{ required: true }]}>
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Button block className="touch" loading={loading} onClick={queryStatus}>
              查询审核状态
            </Button>
          </Form>
        )}

        {status ? (
          <div style={{ marginTop: 16 }}>
            {status.status === 'pending' ? (
              <Alert
                type="info"
                showIcon
                title="审核中"
                description={`${status.name ? `${status.name}，` : ''}提交成功，实验员审核通过后即可登录。`}
              />
            ) : status.status === 'approved' ? (
              <>
                <Alert
                  type="success"
                  showIcon
                  title="审核已通过"
                  description={`${status.name ? `${status.name}，` : ''}可以直接登录进入面板。`}
                />
                <Button type="primary" block className="touch" style={{ marginTop: 12 }} onClick={() => nav('/login')}>
                  去登录
                </Button>
              </>
            ) : (
              <>
                <Alert
                  type="error"
                  showIcon
                  title="审核未通过"
                  description={status.reject_reason || '未填写原因'}
                />
                <p className="meta" style={{ margin: '12px 0 0' }}>
                  修改下面信息后重新提交，账号和密码保持不变。
                </p>
                <Form form={resubmitForm} layout="vertical" style={{ marginTop: 12 }}>
                  <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="role" label="身份">
                    <Radio.Group options={ROLE_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="class_name" label="班级 / 教研组" rules={[{ max: 30 }]}>
                    <Input placeholder="高一二班 / 信息技术组" />
                  </Form.Item>
                  <Button block className="touch" loading={loading} onClick={submitResubmit}>
                    修改并重新提交
                  </Button>
                </Form>
              </>
            )}
          </div>
        ) : null}

        <div className="auth-links">
          <Link to="/login">已有账号，去登录</Link>
          <Link to="/">返回首页</Link>
        </div>
        <p className="auth-footnote">成都市第二十中学校 · 四川省成都市金牛区育英路6号</p>
        </div>
      </main>
    </div>
  );
}
